import { useMemo } from 'react';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import { IGuest_Invitation, Rsvp } from '@bridebook/models/source/models/Weddings/Guests.types';
import { useSelector } from '@bridebook/web/lib/utils';
import { useBBSuppliers } from '@bridebook/web/lib/supplier/hooks/query/use-bb-suppliers';
import {
  getWeddingId,
  getWebsiteDraftWebsite,
  getWebsitePartners,
  getWebsiteWeddingDate,
} from '@bridebook/web/lib/weddings/selectors';
import { useWeddingEvents } from '@bridebook/web/lib/wedding-website/hooks/query';
import { useWeddingRsvp } from '@bridebook/web/lib/rsvp/query/hooks/wedding/use-wedding-rsvp';
import { getEnrichedEvents } from '@bridebook/web/lib/rsvp/utils/get-enriched-events';
import { isCustomEvent } from '@bridebook/web/lib/events/types';
import { isPrimaryVenueSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { GetWeddingWebsiteDataResponseSuccess } from '@bridebook/web/pages/api/for/website/[websiteId]';

export type RsvpPreviewData = ReturnType<typeof useRsvpPreviewData>;

export const useRsvpPreviewData = () => {
  const { data: weddingRsvp, isLoading: isWeddingRsvpLoading } = useWeddingRsvp();
  const { data: allEvents = [], isLoading: isWeddingEventsLoading } = useWeddingEvents();

  const website = useSelector(getWebsiteDraftWebsite);
  const partners = useSelector(getWebsitePartners);
  const weddingDate = useSelector(getWebsiteWeddingDate);
  const weddingId = useSelector(getWeddingId);

  const visibleEvents = useMemo(() => allEvents.filter((event) => event.featured), [allEvents]);

  const supplierIds = useMemo(() => {
    const primaryVenue = website.sections.find(isPrimaryVenueSection);
    const customSuppliers = visibleEvents.filter(isCustomEvent).map((event) => event.supplier);
    return uniq(compact([primaryVenue?.data.supplier, ...customSuppliers]));
  }, [visibleEvents, website.sections]);

  const { data: suppliers = [], isLoading: isSuppliersLoading } = useBBSuppliers({ supplierIds });

  const weddingWebsiteData = useMemo<GetWeddingWebsiteDataResponseSuccess>(
    () => ({
      resultCode: 'ok',
      weddingId,
      website,
      events: visibleEvents,
      suppliers,
      photosBySupplier: [],
      wedding: {
        partners,
        date: weddingDate,
      },
      rsvp: {
        deadline: weddingRsvp?.deadline,
      },
    }),
    [weddingId, partners, suppliers, visibleEvents, website, weddingDate, weddingRsvp?.deadline],
  );

  const guestInvitation = useMemo<IGuest_Invitation>(
    () => ({
      events: visibleEvents.reduce<IGuest_Invitation['events']>((acc, event) => {
        acc[event.id] = { rsvp: Rsvp.PENDING };
        return acc;
      }, {}),
      note: '',
      questions: {},
    }),
    [visibleEvents],
  );

  const enrichedEvents = useMemo(
    () => getEnrichedEvents(Object.keys(guestInvitation.events), weddingWebsiteData, visibleEvents),
    [guestInvitation.events, weddingWebsiteData, visibleEvents],
  );

  return {
    isLoading: isWeddingRsvpLoading || isWeddingEventsLoading || isSuppliersLoading,
    weddingRsvp,
    weddingWebsiteData,
    guestInvitation,
    enrichedEvents,
  };
};
